import React, { useState, useCallback } from "react";
import { ProductStats } from "./ProductStats";
import { ProductControls } from "./ProductControls";
import { ProductTable } from "./ProductTable";

import { useProducts } from "@/hooks/useProducts";
import { useCategories } from "@/hooks/useCategories";
import { useArtistDetails } from "@/hooks/useArtistDetails";
import type { Product } from "@/hooks/useProducts";

export default function ProductManagement() {
  const { 
    products, 
    loading: productsLoading, 
    error: productsError, 
    createProduct, 
    updateProduct, 
    deleteProduct 
  } = useProducts();
  
  const { 
    categories, 
    loading: categoriesLoading, 
    error: categoriesError, 
    getActiveCategories 
  } = useCategories();
  
  const { 
    artistDetails, 
    loading: artistDetailsLoading, 
    error: artistDetailsError 
  } = useArtistDetails();

  
  
  const [searchTerm, setSearchTerm] = useState("");
  const [selectedCategory, setSelectedCategory] = useState("all");
  const [editingProduct, setEditingProduct] = useState<Product | null>(null);

  const loading = productsLoading || categoriesLoading || artistDetailsLoading;
  const error = productsError || categoriesError || artistDetailsError;

  // Filter products based on search and category
  const filteredProducts = products.filter((product) => {
    const matchesSearch = product.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
                         product.description?.toLowerCase().includes(searchTerm.toLowerCase()) ||
                         (product.artistid && artistDetails.find(a => a.artistid === product.artistid)?.full_name.toLowerCase().includes(searchTerm.toLowerCase()));
    
    let matchesCategory = selectedCategory === "all";
    if (selectedCategory !== "all") {
      const categoryObj = getActiveCategories().find(cat => cat.name === selectedCategory);
      matchesCategory = categoryObj ? product.category === categoryObj.slug : false;
    }
    
    return matchesSearch && matchesCategory;
  });

  const handleEdit = useCallback((product: Product) => {
    setEditingProduct(product);
  }, []);

  const handleFormClose = useCallback(() => {
    setEditingProduct(null);
  }, []);

  // Show error if any
  if (error) {
    return (
      <div className="p-6">
        <div className="text-center py-8">
          <h2 className="text-xl font-semibold text-red-600 mb-2">Error Loading Data</h2>
          <p className="text-gray-600">{error}</p>
        </div>
      </div>
    );
  }

  return (
    <div className="p-6">
      <div className="mb-6">
        <h1 className="text-3xl font-bold text-gray-900">Brutt Products</h1>
        <p className="text-gray-600 mt-2">Manage your product catalog</p>
      </div>



      <ProductStats 
        products={products}
        categories={getActiveCategories()}
      />

      <ProductControls 
        categories={getActiveCategories()}
        artistDetails={artistDetails}
        createProduct={createProduct}
        updateProduct={updateProduct}
        searchTerm={searchTerm}
        setSearchTerm={setSearchTerm}
        selectedCategory={selectedCategory}
        setSelectedCategory={setSelectedCategory}
        editingProduct={editingProduct}
        onFormClose={handleFormClose}
      />

      <ProductTable 
        products={filteredProducts}
        categories={getActiveCategories()}
        artistDetails={artistDetails}
        loading={loading}
        onEdit={handleEdit}
        onDelete={deleteProduct}
      />
    </div>
  );
}
